import { onSnapshot } from 'firebase/firestore';

// onSnapshot with retry — a Firestore listener that errors DIES permanently,
// and two races make that routine here:
//   1. Joining a squad: the client's user-doc write flips the UI instantly
//      (latency compensation) and membership-gated listeners attach before the
//      server has committed the write the rules check — permission-denied.
//   2. Cold start: listeners that require auth attach before anonymous
//      sign-in completes.
// Both self-heal in a second or two — but only if someone re-attaches. This
// wrapper retries with backoff instead of leaving the squad looking empty
// until an app restart (the "joined DEMO and nothing happened" bug).
// Never gives up while mounted: cold start on a returning member can take
// several seconds for the auth token to settle (the broadcasters listener was
// observed failing twice before succeeding), and a listener that quits after a
// fixed count leaves the squad empty until the component remounts.
export function resilientSnapshot(refOrQuery, onNext, { label = 'listener' } = {}) {
  let unsub = null;
  let stopped = false;
  let attempt = 0;
  let timer = null;

  const attach = () => {
    if (stopped) return;
    unsub = onSnapshot(refOrQuery, (snap) => {
      attempt = 0; // healthy again — future errors get a fresh retry budget
      onNext(snap);
    }, (err) => {
      if (stopped) return;
      // 500ms, 1s, 2s, 4s, then every 5s for as long as we're mounted.
      // (The join race is now solved at the source — squadId waits for the
      // server ack — so this mostly covers cold-start token settling.)
      const delay = Math.min(5000, 500 * Math.pow(2, attempt));
      attempt++;
      console.warn(`[${label}] snapshot error — retry ${attempt} in ${delay}ms:`, err.code || err.message);
      timer = setTimeout(attach, delay);
    });
  };

  attach();
  return () => {
    stopped = true;
    if (timer) clearTimeout(timer);
    if (unsub) unsub();
  };
}
